import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LatestPostsPage.css';

function LatestPostsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 20;

  useEffect(() => {
    fetchLatestPosts();
  }, []);

  const fetchLatestPosts = async () => {
    try {
      setLoading(true);
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/posts/latest`);

      if (!response.ok) {
        throw new Error('최신 글을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setPosts(data.posts || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching latest posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // XE 날짜 형식: 20040630210542 -> YYYY-MM-DD HH:MM
    if (!dateString) return '';
    const str = String(dateString);
    const year = str.substring(0, 4);
    const month = str.substring(4, 6);
    const day = str.substring(6, 8);
    const hour = str.substring(8, 10);
    const minute = str.substring(10, 12);
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const getBoardName = (boardId) => {
    const boardNames = {
      'sermon': '설교모음',
      'mark': '매일묵상',
      'notice': '알림방',
      'free': '사랑채',
      'critique': '설교비평',
      'pannenberg': '판넨베르크 아카이브',
      'member': '후원안내'
    };
    return boardNames[boardId] || null;
  };

  // 한글 게시판만 필터링
  const getKoreanPosts = (allPosts) => {
    return allPosts.filter(post => getBoardName(post.module_srl) !== null);
  };

  // 한글 게시판만 필터링
  const koreanPosts = getKoreanPosts(posts);

  // 페이지네이션
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = koreanPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(koreanPosts.length / postsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="latest-posts-page">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="latest-posts-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="latest-posts-page">
      <div className="container">
        <h1 className="page-title">최신 등록글</h1>

        <div className="posts-stats">
          <p>전체 <strong>{koreanPosts.length}</strong>개의 글</p>
        </div>

        <div className="posts-table-wrapper">
          <table className="posts-table">
            <thead>
              <tr>
                <th className="col-board">게시판</th>
                <th className="col-title">제목</th>
                <th className="col-author">작성자</th>
                <th className="col-date">작성일</th>
                <th className="col-views">조회</th>
              </tr>
            </thead>
            <tbody>
              {currentPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-posts">등록된 글이 없습니다.</td>
                </tr>
              ) : (
                currentPosts.map((post) => (
                  <tr key={post.document_srl}>
                    <td className="col-board">
                      <Link to={`/board/${post.module_srl}`}>
                        {getBoardName(post.module_srl)}
                      </Link>
                    </td>
                    <td className="col-title">
                      <Link to={`/board/${post.module_srl}/${post.document_srl}`}>
                        {post.title}
                        {post.comment_count > 0 && (
                          <span className="comment-count"> [{post.comment_count}]</span>
                        )}
                      </Link>
                    </td>
                    <td className="col-author">{post.nick_name || post.user_name}</td>
                    <td className="col-date">{formatDate(post.regdate)}</td>
                    <td className="col-views">{post.readed_count || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              이전
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // 현재 페이지 주변 5페이지만 표시
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? 'page-btn active' : 'page-btn'}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <span key={pageNumber}>...</span>;
              }
              return null;
            })}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LatestPostsPage;
